import type React from "react";
import { useState } from "react";

// Yes/No confirm buttons for the confirm-gate turn of a portal workflow (e.g.
// "Want me to place this order?"). Tapping a button sends its message down the
// SAME path a typed "go ahead" takes (onSendFollowup) — the model's confirm
// gate is satisfied without the clinician having to type. Collapses after a
// choice so it can't be double-submitted.
export interface WorkflowConfirmData {
  confirm_label?: string;
  decline_label?: string;
  confirm_message?: string;
  decline_message?: string;
}

interface WorkflowConfirmProps {
  data: WorkflowConfirmData;
  onSendFollowup: (message: string) => void;
  disabled?: boolean;
}

const WorkflowConfirm: React.FC<WorkflowConfirmProps> = ({ data, onSendFollowup, disabled }) => {
  const [choseLabel, setChoseLabel] = useState<string | null>(null);

  const confirmLabel = data?.confirm_label || "Yes, go ahead";
  const declineLabel = data?.decline_label || "No, not now";
  const confirmMessage = data?.confirm_message || confirmLabel;
  const declineMessage = data?.decline_message || declineLabel;

  const choose = (label: string, message: string) => {
    if (disabled || choseLabel) return;
    setChoseLabel(label);
    onSendFollowup(message);
  };

  if (choseLabel) {
    return (
      <div style={styles.container}>
        <span style={styles.chosen}>{choseLabel}</span>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => choose(confirmLabel, confirmMessage)}
        style={{ ...styles.button, ...styles.confirm, ...(disabled ? styles.disabled : {}) }}
      >
        {confirmLabel}
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => choose(declineLabel, declineMessage)}
        style={{ ...styles.button, ...styles.decline, ...(disabled ? styles.disabled : {}) }}
      >
        {declineLabel}
      </button>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap", alignItems: "center" },
  button: {
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid transparent",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  confirm: { backgroundColor: "#4338CA", color: "#FFFFFF", borderColor: "#4338CA" },
  decline: { backgroundColor: "#FFFFFF", color: "#374151", borderColor: "#D1D5DB" },
  disabled: { opacity: 0.6, cursor: "not-allowed" },
  chosen: { fontSize: "13px", fontWeight: 500, color: "#4338CA" },
};

export default WorkflowConfirm;
